import { Injectable } from '@angular/core';
import { ApiClientHelper } from '../helpers/api-client.helper';
import { Observable } from 'rxjs';
import { AppConfig } from '../../shared/config/app-config';
import { SmileIdPayload } from '../../kamona/model/kamonal-model';
import { LocalStorageService } from './local-storage.service';
import { LoginTypeRequest } from '../models/forget-passcode';
import { GenerateReferenceApiPayload } from '@mpt-fo/common/shared/models/device.model';

export enum LivelinessErrorCode {
    LIVELINESS_REFERENCE_CONSUMED = '401035',
}

@Injectable({
    providedIn: 'root',
})
export class LivelinessService {
    constructor(private apiClientHelper: ApiClientHelper, public config: AppConfig, private localStorage: LocalStorageService) {}

    profileUpdateFlowId: string;

    getChangePasswordMetaMapReference(): Observable<any> {
        // deprecated
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/settings/password/generate-reference`;
        return this.apiClientHelper.getForObservable(url);
    }

    getResetUserPinMetaMapReference(): Observable<any> {
        // deprecated
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/settings/user-pin/generate-reference`;
        return this.apiClientHelper.getForObservable(url);
    }

    getStaffOnboardingReference(activationToken: string, livenessPurpose: string, deviceUniqueIdentifier: string): Observable<any> {
        const url =
            this.config.MONIEPOINT_USER_URL +
            `/api/v1/settings/staff-onboarding/generate-reference/${activationToken}?livenessPurpose=${livenessPurpose}&deviceUniqueIdentifier=${deviceUniqueIdentifier}`;
        return this.apiClientHelper.getForObservable(url);
    }

    getAccountClosureMetaMapReference(deviceUniqueIdentifier: string): Observable<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/settings/generate-reference?livelinessPurpose=ACCOUNT_CLOSURE&deviceUniqueIdentifier=${deviceUniqueIdentifier}`;
        return this.apiClientHelper.getForObservable(url);
    }

    getChangePasswordMetaMapStatus(reference: string): Observable<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/settings/password-change/${reference}/status`;
        return this.apiClientHelper.getForObservable(url);
    }

    getResetUserPinMetaMapStatus(reference: string): Observable<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/settings/user-pin/${reference}/status`;
        return this.apiClientHelper.getForObservable(url);
    }

    postResetPasswordMetaMapStatus(reference: string, username: string): Observable<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/settings/password-recovery/${reference}/status`;
        return this.apiClientHelper.postForObservable(url, { username }, false);
    }

    postDeviceRegistrationMetaMapStatus(reference: string, username: string): Observable<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/settings/device-registration/${reference}/status`;
        return this.apiClientHelper.postForObservable(url, { username }, false);
    }

    postStaffOnboardingMetaMapStatus(reference: string, activationToken: string, livenessPurpose: string): Observable<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/settings/staff-onboarding/${reference}/status`;
        return this.apiClientHelper.postForObservable(url, { activationToken, livenessPurpose }, false);
    }

    getAccountClosureMetaMapStatus(reference: string): Observable<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/settings/liveliness/${reference}/status?livelinessPurpose=ACCOUNT_CLOSURE`;
        return this.apiClientHelper.getForObservable(url);
    }

    getProfileUpdateMetaMapStatus(reference: string): Observable<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/profile-update/validate/FACE_MATCH_CHECK`;
        return this.apiClientHelper.postForObservable(url, { reference });
    }

    postZendeskTicket(reference: string): Observable<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/zendesk/tickets`;
        return this.apiClientHelper.postForObservable(url, { reference }, false);
    }

    uploadSmileIdImages(payload: SmileIdPayload): Observable<any> {
        const urlPath = `/api/v1/smile/submit-job`;
        const url = this.config.MONIEPOINT_USER_URL + urlPath;
        return this.apiClientHelper.postForObservable(url, payload, false);
    }

    submitSmileIDJob(payload: SmileIdPayload): Promise<any> {
        const urlPath = `/api/v1/smile/submit-job`;
        const url = this.config.MONIEPOINT_USER_URL + urlPath;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    generateLivelinessReference(livelinessPurpose: string, deviceUniqueIdentifier: string): Observable<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/settings/generate-reference?livelinessPurpose=${livelinessPurpose}&deviceUniqueIdentifier=${deviceUniqueIdentifier}`;
        return this.apiClientHelper.getForObservable(url);
    }

    getLivelinessStatus(reference: string, livelinessPurpose: string): Observable<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/settings/liveliness/${reference}/status?livelinessPurpose=${livelinessPurpose}`;
        return this.apiClientHelper.getForObservable(url);
    }

    generateLivelinessReferenceBrm(businessOwnerId: string, livelinessPurpose: string, deviceUniqueIdentifier: string): Observable<any> {
        const url =
            this.config.MONIEPOINT_USER_URL +
            `/api/v1/settings/brm/generate-reference?businessOwnerId=${businessOwnerId}&livelinessPurpose=${livelinessPurpose}&deviceUniqueIdentifier=${deviceUniqueIdentifier}`;
        return this.apiClientHelper.getForObservable(url);
    }

    getLivelinessStatusBrm(reference: string, businessOwnerId: string, livelinessPurpose: string): Observable<any> {
        const url =
            this.config.MONIEPOINT_USER_URL +
            `/api/v1/settings/brm/liveliness/${reference}/status?businessOwnerId=${businessOwnerId}&livelinessPurpose=${livelinessPurpose}`;
        return this.apiClientHelper.getForObservable(url);
    }

    validatReference(reference: string, livelinessPurpose: string): Observable<any> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/settings/liveliness/${reference}/status?livelinessPurpose=${livelinessPurpose}`;
        return this.apiClientHelper.getForObservable(url);
    }

    validatReferencePublic(reference: string, livelinessPurpose: string, username: string): Observable<any> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/settings/face-match/${reference}/status?livenessPurpose=${livelinessPurpose}`;
        return this.apiClientHelper.postForObservable(url, this.buildValidatReferencePayload(username), false);
    }

    buildValidatReferencePayload(username: string) {
        const userTypePayload = this.getUserTypePayload();
        const usePhoneNumberForRecovery = this.isPhoneNumberLoginType(userTypePayload);
        const payload = usePhoneNumberForRecovery ? { phoneNumber: username } : { username: username };
        return payload;
    }

    getUserTypePayload() {
        return this.localStorage.getUserTypePayload();
    }

    isPhoneNumberLoginType(userTypePayload) {
        return userTypePayload?.identityType === LoginTypeRequest.PHONENUMBER;
    }

    generateLivelinessReferenceFromAuth(authenticationPurpose: string, payload: GenerateReferenceApiPayload): Observable<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v1/auth/liveness/generate-reference?authenticationPurpose=${authenticationPurpose}`;
        return this.apiClientHelper.postForObservable(url, payload);
    }

}
